import { AUTH_TOKEN } from 'app/constants';
import { PaginationResponseType } from 'app/console-api';
import { ExtendedOffersResponse } from 'app/console-api/offers/offers-extended';
import { HttpError, HttpErrorProps } from 'app/components/utils/http-error';
import { PaginationParams } from 'app/console-api/utils/pagination';
import { UrlFilterController } from 'app/components/filters/url-filters';
import { IsActiveAdapter } from 'app/components/filters/is-active-filter';
import { TaskTypeAdapter } from 'app/components/filters/task-type-filter';
import { DealTypesAdapter } from 'app/components/filters/deal-types-filter';

export type TradeType = 'exchange' | 'buy' | 'sell' | 'internal';

export interface DealTypeOption {
  value: TradeType;
  label: string;
}

export const DEAL_TYPE_OPTIONS: DealTypeOption[] = [
  { value: 'exchange', label: 'Exchange' },
  { value: 'buy', label: 'Buy' },
  { value: 'sell', label: 'Sell' }
];

export const formatDealType = (type: TradeType): string => {
  const option = DEAL_TYPE_OPTIONS.find((opt) => opt.value === type);
  return option?.label ?? type;
};

export interface CreateOfferRequest {
  pluginID: number;
  name: string;
  description: string;
  tradeType: TradeType;
  pluginMetadata: object | null;
  customMetadata: object | null;
  isPublic: boolean;
  isPublicStats: boolean;
  minQuota: number;
}

export interface UpdateOfferRequest {
  isActive: boolean;
  name: string;
  description: string;
  tradeType: TradeType;
  customMetadata: object | null;
  isPublic: boolean;
  isPublicStats: boolean;
}

export interface Offer {
  id: string;
  organizationID: string;
  appID: string;
  pluginID: number;
  name: string;
  description: string;
  tradeType: TradeType;
  pluginMetadata: object | null;
  customMetadata: object;
  isActive: boolean;
  isPublic: boolean;
  isPublicStats: boolean;
  minQuota: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface GetOffersListFilter extends PaginationParams {
  isActive?: boolean;
}

export const createOffer = async (appID: string, data: CreateOfferRequest): Promise<Offer> => {
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${appID}/offers`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    if (response.body) {
      return response
        .clone()
        .json()
        .then((errorInfo: HttpErrorProps) => {
          return Promise.reject(
            new HttpError(
              `Failed to create task: ${response.statusText}`,
              response.status,
              errorInfo
            )
          );
        });
    } else {
      return Promise.reject(
        new HttpError(`Failed to create task: ${response.statusText}`, response.status)
      );
    }
  }
  return await response.json();
};

export const getOffers = async (
  appID: string,
  ctl?: UrlFilterController,
  pagination?: PaginationParams
): Promise<PaginationResponseType<ExtendedOffersResponse>> => {
  let searchParams = new URLSearchParams();
  if (ctl) {
    searchParams = ctl.buildSearchParams([
      { adapter: IsActiveAdapter, value: ctl.get(IsActiveAdapter) },
      { adapter: TaskTypeAdapter, value: ctl.get(TaskTypeAdapter) },
      { adapter: DealTypesAdapter, value: ctl.get(DealTypesAdapter) }
    ]);
  }

  if (!pagination) {
    pagination = {
      pageSize: Number(process.env.REACT_APP_PAGE_SIZE_ITEMS),
      currentPage: 1
    };
  }
  searchParams.append('pageSize', pagination.pageSize.toString());
  searchParams.append('currentPage', pagination.currentPage.toString());
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${appID}/offers?${searchParams.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
      }
    }
  );
  if (!response.ok) {
    return Promise.reject(
      new HttpError(`Failed to get tasks list: ${response.statusText}`, response.status)
    );
  }
  return await response.json();
};

export const getOfferById = async (appID: string, id: string): Promise<Offer> => {
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${appID}/offers/${id}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
      }
    }
  );
  if (!response.ok) {
    return Promise.reject(
      new HttpError(`Failed to get task by id: ${response.statusText}`, response.status)
    );
  }
  return await response.json();
};

export const updateOffer = async (
  appID: string,
  id: string,
  data: UpdateOfferRequest
): Promise<Offer> => {
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${appID}/offers/${id}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    if (response.body) {
      return response
        .clone()
        .json()
        .then((errorInfo: HttpErrorProps) => {
          return Promise.reject(
            new HttpError(
              `Failed to update task: ${response.statusText}`,
              response.status,
              errorInfo
            )
          );
        });
    } else {
      return Promise.reject(
        new HttpError(`Failed to update task: ${response.statusText}`, response.status)
      );
    }
  }

  return await response.json();
};
